/**
 * Utilities for handling social link URLs and usernames
 */

// Platform URL prefixes for username-only input
export const PLATFORM_URL_PREFIXES: Record<string, string> = {
  instagram: 'https://instagram.com/',
  'x-twitter': 'https://x.com/',
  facebook: 'https://facebook.com/',
  youtube: 'https://youtube.com/@',
  tiktok: 'https://tiktok.com/@',
  linkedin: 'https://linkedin.com/',
  pinterest: 'https://pinterest.com/',
  bluesky: 'https://bsky.app/profile/',
  threads: 'https://threads.net/@',
};

// Platforms that use username-only input (all except website and other)
export const USERNAME_ONLY_PLATFORMS = new Set([
  'instagram',
  'x-twitter',
  'facebook',
  'youtube',
  'tiktok',
  'linkedin',
  'pinterest',
  'bluesky',
  'threads',
]);

/**
 * Per-platform allowed-character patterns for a bare social handle (after any
 * platform-specific prefix like LinkedIn's `in/` has been stripped). These
 * mirror each platform's real username rules and intentionally exclude spaces,
 * slashes, `@`, and protocol characters so pasted URLs and spaced usernames are
 * rejected outright rather than silently producing a broken profile link.
 */
const SOCIAL_HANDLE_PATTERNS: Record<string, RegExp> = {
  instagram: /^[A-Za-z0-9._]+$/,
  threads: /^[A-Za-z0-9._]+$/,
  'x-twitter': /^[A-Za-z0-9_]+$/,
  facebook: /^[A-Za-z0-9.]+$/,
  linkedin: /^[A-Za-z0-9._-]+$/,
  youtube: /^[A-Za-z0-9._-]+$/,
  tiktok: /^[A-Za-z0-9._]+$/,
  pinterest: /^[A-Za-z0-9_-]+$/,
  bluesky: /^[A-Za-z0-9.-]+$/,
};

/** Machine-readable reason a social handle failed validation. */
export type SocialHandleErrorCode = 'whitespace' | 'at_symbol' | 'url' | 'bluesky_suffix' | 'invalid';

interface SocialHandleValidationResult {
  valid: boolean;
  code?: SocialHandleErrorCode;
}

// Bare hostnames users commonly paste into a handle field. A value that IS one
// of these (optionally `www.`-prefixed) is a URL fragment, not a handle, even
// when it has no slash — e.g. "instagram.com" -> "https://instagram.com/instagram.com".
// `bsky.social` is intentionally excluded: it is the suffix of a valid Bluesky
// handle (`alice.bsky.social`), not a paste-able profile host.
const SOCIAL_DOMAINS = new Set([
  'instagram.com',
  'facebook.com',
  'fb.com',
  'linkedin.com',
  'x.com',
  'twitter.com',
  'threads.net',
  'threads.com',
  'youtube.com',
  'youtu.be',
  'tiktok.com',
  'pinterest.com',
  'bsky.app',
]);

// How many path segments a real profile URL has on each network. A profile is a
// single slug deep (`instagram.com/jane`) except LinkedIn (`in/jane`,
// `company/acme`) and Bluesky (`profile/handle`). Anything deeper is a
// non-profile path — `instagram.com/reel/x`, `instagram.com/p/x`,
// `x.com/i/status/123` — whose first segment (`reel`, `p`, `i`) is NOT a handle.
// Normalizing those would silently submit a broken profile link, so we leave the
// value untouched and let validation flag it as a URL.
const PROFILE_PATH_DEPTH: Record<string, number> = {
  instagram: 1,
  facebook: 1,
  'x-twitter': 1,
  threads: 1,
  youtube: 1,
  tiktok: 1,
  pinterest: 1,
  linkedin: 2,
  bluesky: 2,
};

// Hostnames (sans `www.`) that belong to each platform. A pasted URL whose host
// is in its OWN platform's set is the user supplying their own profile link, so
// we can safely strip it to the bare handle. Cross-network hosts are absent on
// purpose: an x.com URL pasted into the Instagram field stays a URL and still
// fails validation rather than being silently converted. Threads serves both
// `threads.net` and `threads.com`, so both map to the Threads field.
const PLATFORM_HOSTNAMES: Record<string, Set<string>> = {
  instagram: new Set(['instagram.com']),
  facebook: new Set(['facebook.com', 'fb.com']),
  linkedin: new Set(['linkedin.com']),
  'x-twitter': new Set(['x.com', 'twitter.com']),
  threads: new Set(['threads.net', 'threads.com']),
  youtube: new Set(['youtube.com', 'youtu.be']),
  tiktok: new Set(['tiktok.com']),
  pinterest: new Set(['pinterest.com']),
  bluesky: new Set(['bsky.app']),
};

/**
 * Validates a username-only social handle (e.g. Instagram, X, Threads).
 *
 * Empty input is treated as VALID because the field is optional — callers are
 * expected to filter empty values out before submit. A non-empty value must be
 * a bare handle, NOT a full URL or a username containing spaces (the two inputs
 * that were creating broken links on public profiles).
 *
 * Returns a machine-readable `code` so each surface can render its own copy
 * (the welcome wizard maps codes to i18n strings; other callers can map to
 * their own messages). The order of checks is chosen so the most specific,
 * user-actionable message wins (whitespace > URL > @ > platform rule).
 */
export function validateSocialHandle(rawUsername: string, platform: string): SocialHandleValidationResult {
  const username = (rawUsername ?? '').trim();

  // Optional field — empty is always acceptable.
  if (username.length === 0) return { valid: true };

  // Only platforms that take a username (not a free-form website/other URL).
  if (!USERNAME_ONLY_PLATFORMS.has(platform)) return { valid: false, code: 'invalid' };

  // Reject any whitespace (spaces, tabs) anywhere — checked first so it wins
  // even for the Facebook `profile.php?id=` form below.
  if (/\s/.test(username)) return { valid: false, code: 'whitespace' };

  // Facebook lets users paste the numeric `profile.php?id=<digits>` form.
  if (platform === 'facebook' && username.startsWith('profile.php?id=')) {
    const id = username.slice('profile.php?id='.length);
    return /^\d+$/.test(id) ? { valid: true } : { valid: false, code: 'invalid' };
  }

  // Strip LinkedIn's legitimate `in/`/`company/` path prefix before the slash
  // check. Match `constructUrlFromUsername` exactly (case-SENSITIVE) so any
  // form we accept here is also constructed correctly — `In/foo` is therefore
  // treated as URL-shaped and rejected rather than yielding `linkedin.com/in/In/foo`.
  let handle = username;
  if (platform === 'linkedin') {
    const prefixMatch = /^(in|company)\//.exec(handle);
    if (prefixMatch) handle = handle.slice(prefixMatch[0].length);
  }

  // Reject full URLs (with or without protocol) and bare platform hostnames:
  // "https://instagram.com/x", "instagram.com/x" (any slash), or "instagram.com".
  const hostCandidate = username.toLowerCase().replace(/^www\./, '');
  if (
    /^https?:\/\//i.test(username) ||
    username.includes('://') ||
    handle.includes('/') ||
    SOCIAL_DOMAINS.has(hostCandidate)
  ) {
    return { valid: false, code: 'url' };
  }

  // Handles must be entered without an `@`.
  if (username.includes('@')) return { valid: false, code: 'at_symbol' };

  // Bluesky handles are `<labels>.bsky.social`, where each DNS label is
  // alphanumeric and may contain internal (not leading/trailing) hyphens.
  if (platform === 'bluesky') {
    const blueskyHandle = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+bsky\.social$/i;
    if (!blueskyHandle.test(username)) return { valid: false, code: 'bluesky_suffix' };
  }

  // Final platform-specific character check on the bare handle.
  const pattern = SOCIAL_HANDLE_PATTERNS[platform];
  if (pattern && !pattern.test(handle)) return { valid: false, code: 'invalid' };

  return { valid: true };
}

/**
 * Extracts username from a URL for a given platform
 */
export function extractUsernameFromUrl(url: string, platform: string): string {
  if (!url || !platform) return '';

  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;

    // Handle different platform patterns
    switch (platform) {
      case 'instagram':
      case 'x-twitter':
      case 'pinterest':
        // Format: /username
        return pathname.split('/').filter((p) => p)[0] || '';
      case 'facebook':
        // Format: /username or /profile.php?id=...
        // For profile.php URLs, return empty string so the full URL is preserved
        if (pathname === '/profile.php' && urlObj.searchParams.has('id')) {
          return '';
        }
        // For regular username URLs, extract username
        return pathname.split('/').filter((p) => p)[0] || '';
      case 'youtube': {
        // Format: /@username (YouTube modern format) or /username (legacy)
        // Prefix already includes @, so extract username without @
        const youtubeUsername = pathname.split('/').filter((p) => p)[0] || '';
        // Remove @ from beginning (prefix has it) and end (invalid)
        return youtubeUsername.replace(/^@/, '').replace(/@$/, '');
      }
      case 'tiktok':
      case 'threads': {
        // Format: /@username (these platforms require @ at beginning in URL, but prefix already has @)
        // Extract username without @ since the prefix will add it
        const parts = pathname.split('/').filter((p) => p);
        const usernamePart = parts[0] || '';
        // Remove @ from beginning (prefix has it) and end (invalid)
        return usernamePart.replace(/^@/, '').replace(/@$/, '');
      }
      case 'linkedin': {
        // Format: /in/username or /company/name
        const linkedinParts = pathname.split('/').filter((p) => p);

        // Check for /company/ path
        const companyIndex = linkedinParts.indexOf('company');
        if (companyIndex >= 0 && companyIndex < linkedinParts.length - 1) {
          const companyName = linkedinParts[companyIndex + 1] ?? '';
          // Decode URL-encoded characters
          try {
            return `company/${decodeURIComponent(companyName)}`;
          } catch {
            return `company/${companyName}`;
          }
        }

        // Check for /in/ path
        const inIndex = linkedinParts.indexOf('in');
        if (inIndex >= 0 && inIndex < linkedinParts.length - 1) {
          const username = linkedinParts[inIndex + 1] ?? '';
          // Decode URL-encoded characters
          try {
            return `in/${decodeURIComponent(username)}`;
          } catch {
            return `in/${username}`;
          }
        }

        return '';
      }
      case 'bluesky': {
        // Format: /profile/username.bsky.social
        const bskyParts = pathname.split('/').filter((p) => p);
        const profileIndex = bskyParts.indexOf('profile');
        if (profileIndex >= 0 && profileIndex < bskyParts.length - 1) {
          return bskyParts[profileIndex + 1] ?? '';
        }
        return '';
      }
      default:
        return '';
    }
  } catch {
    // If URL parsing fails, try simple string extraction
    const prefix = PLATFORM_URL_PREFIXES[platform];
    if (prefix && url.startsWith(prefix)) {
      const remainder = url.slice(prefix.length);
      // Remove query params and hash
      let username = remainder.split('?')[0]?.split('#')[0]?.split('/')[0] ?? '';
      // Remove @ from beginning or end
      username = username.replace(/^@/, '').replace(/@$/, '');
      return username;
    }
    return '';
  }
}

/**
 * Normalizes input for a username-only field by auto-stripping a pasted profile
 * URL of the SAME network down to its bare handle — the obvious user intent when
 * someone pastes e.g. `https://instagram.com/jane` (or `instagram.com/jane`) into
 * the Instagram input.
 *
 * Only same-network URLs with an extractable handle are converted. Everything
 * else is returned UNCHANGED so existing validation still applies:
 *  - a plain handle (incl. one with a dot like `jane.doe`) — no host match;
 *  - a cross-network URL (x.com pasted into Instagram) — host not in the set;
 *  - a bare host with no path (`instagram.com`) — nothing to extract.
 */
export function normalizeSocialHandleInput(value: string, platform: string): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed || !USERNAME_ONLY_PLATFORMS.has(platform)) return value;

  const hostnames = PLATFORM_HOSTNAMES[platform];
  if (!hostnames) return value;

  // `extractUsernameFromUrl` needs a parseable URL; protocol-less input like
  // `instagram.com/jane` gets `https://` prepended first.
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  let host: string;
  let pathname: string;
  let searchParams: URLSearchParams;
  try {
    const url = new URL(withProtocol);
    host = url.hostname.toLowerCase().replace(/^www\./, '');
    pathname = url.pathname;
    searchParams = url.searchParams;
  } catch {
    return value;
  }

  // Wrong network, or a bare host with no profile path — leave it for validation.
  if (!hostnames.has(host) || pathname === '' || pathname === '/') return value;

  // A path deeper than a profile slug (`/reel/x`, `/p/x`, `/i/status/123`) is a
  // non-profile URL whose first segment is not a handle — leave it for validation
  // rather than stripping it to a bogus handle like `reel`/`p`/`i`.
  const depth = pathname.split('/').filter(Boolean).length;
  if (depth > (PROFILE_PATH_DEPTH[platform] ?? 1)) return value;

  // Facebook's numeric profile URL (`/profile.php?id=<digits>`) has no slug for
  // `extractUsernameFromUrl` to return. Collapse it to the bare
  // `profile.php?id=<digits>` form that `validateSocialHandle` accepts, instead
  // of leaving the full URL (which would then be rejected as a URL).
  if (platform === 'facebook' && pathname === '/profile.php') {
    const id = searchParams.get('id') ?? '';
    return /^\d+$/.test(id) ? `profile.php?id=${id}` : value;
  }

  let handle = extractUsernameFromUrl(withProtocol, platform);
  if (!handle) return value;

  // Mirror the `linkedin.com/in/` prefix shown in the UI: collapse the extracted
  // `in/slug` to the bare slug (a `company/slug` stays so the prefix can flip).
  if (platform === 'linkedin' && handle.startsWith('in/')) {
    handle = handle.slice('in/'.length);
  }

  return handle;
}

/**
 * The bare handle an input denotes: a same-network profile URL reduced to its
 * slug by `normalizeSocialHandleInput`, with the `@` people wrap handles in
 * dropped from either end (`constructUrlFromUsername` drops both anyway).
 * NOT validated — callers that need a verdict run `validateSocialHandle` on
 * the result, so an in-progress value survives editing.
 */
export function socialHandleFromInput(value: string, platform: string): string {
  return normalizeSocialHandleInput(value, platform).replace(/^@|@$/g, '');
}

/** That handle when the platform's own rules accept it, else null. */
export function parseSocialHandle(value: string, platform: string): string | null {
  const handle = socialHandleFromInput(value, platform);
  if (!handle || !validateSocialHandle(handle, platform).valid) {
    return null;
  }
  return handle;
}

/**
 * Constructs a full URL from a username for a given platform
 */
export function constructUrlFromUsername(username: string, platform: string): string {
  if (!username || !platform) return '';

  const urlPrefix = PLATFORM_URL_PREFIXES[platform];
  if (!urlPrefix) return '';

  // Clean username based on platform requirements
  let cleanedUsername = username.trim();

  // Special handling for LinkedIn - preserve in/ or company/ prefix if present
  if (platform === 'linkedin') {
    if (cleanedUsername.startsWith('in/') || cleanedUsername.startsWith('company/')) {
      // User has already specified the path type, use it as-is
      return `${urlPrefix}${cleanedUsername}`;
    }
    // Default to /in/ for backward compatibility
    cleanedUsername = cleanedUsername.replace(/^@/, '').replace(/@$/, '');
    return `${urlPrefix}in/${cleanedUsername}`;
  }

  if (platform === 'youtube' || platform === 'tiktok' || platform === 'threads') {
    // YouTube, TikTok, and Threads: prefix already includes @, so remove @ from username to avoid double @
    // Remove @ from beginning (prefix has it) and end (invalid)
    cleanedUsername = cleanedUsername.replace(/^@/, '').replace(/@$/, '');
  } else {
    // Other platforms: remove @ from beginning or end
    cleanedUsername = cleanedUsername.replace(/^@/, '').replace(/@$/, '');
  }

  return `${urlPrefix}${cleanedUsername}`;
}

/**
 * Prepares a social link for API submission by constructing URL from username if needed
 */
export function prepareSocialLinkForApi(link: { type: string; url?: string; username?: string; title?: string }): {
  type: string;
  url: string;
  title?: string;
} {
  const { type, url, username, title } = link;

  // Facebook profile.php URLs with id parameter should use the URL directly
  if (type === 'facebook') {
    // Check if username is in profile.php format (user-entered)
    if (username && username.startsWith('profile.php?id=')) {
      const idValue = username.replace('profile.php?id=', '').trim();
      if (idValue) {
        return { type, url: `https://facebook.com/profile.php?id=${idValue}`, title };
      }
    }
    // Check if URL is a profile.php URL
    if (url) {
      try {
        const urlObj = new URL(url);
        if (urlObj.pathname === '/profile.php' && urlObj.searchParams.has('id')) {
          // Normalize protocol and hostname, but preserve path and id parameter
          return { type, url: `https://facebook.com${urlObj.pathname}${urlObj.search}`, title };
        }
      } catch {
        // If URL parsing fails, continue with normal logic
      }
    }
  }

  // For username-only platforms, construct URL from username
  if (type && USERNAME_ONLY_PLATFORMS.has(type) && username) {
    const constructedUrl = constructUrlFromUsername(username, type);
    if (constructedUrl) {
      return { type, url: constructedUrl, title };
    }
  }

  // For website/other or if URL is already set, use URL directly
  return { type, url: url || '', title };
}

/**
 * Full http(s) href for a stored social row. Handle platforms may store a bare
 * username; website/other rows must already be a URL. Invalid values are dropped
 * rather than emitted as sameAs / anchor hrefs.
 */
export function absoluteSocialHref(link: { type?: string | null; url?: string | null }): string | null {
  const type = (link.type ?? '').trim();
  const raw = (link.url ?? '').trim();
  if (!raw) return null;

  let href = raw;
  if (!/^https?:\/\//i.test(raw)) {
    if (!type || !USERNAME_ONLY_PLATFORMS.has(type)) {
      return null;
    }
    href = constructUrlFromUsername(raw, type);
  }

  try {
    const parsed = new URL(href);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.href;
  } catch {
    return null;
  }
}
